import { useEffect, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { Appearance, Stripe, StripeElementsOptions } from '@stripe/stripe-js';

import { PaymentType, PricingPlanSchema } from 'types';
import { getChannelURL } from 'utils';
import { isMemberSelector, profileChannelNameSelector } from 'store/selectors';
import { api, LoggerService } from 'services';
import { useRouter, useStore } from 'hooks';
import { Payment } from 'containers';
import { Loader, MembershipPlanCard } from 'components';

export type MemberPaymentProps = {
  appearance: Appearance;
  stripePromise: Promise<Stripe | null>;
};

const MemberPayment = ({ appearance, stripePromise }: MemberPaymentProps): JSX.Element => {
  const router = useRouter<{
    channelName?: string;
    payment_intent_client_secret: string;
  }>();
  const { channelName, payment_intent_client_secret: paymentIntentClientSecret } = router.params;
  const { isMember, profileChannelName } = useStore(state => ({
    isMember: isMemberSelector(channelName || '')(state),
    profileChannelName: profileChannelNameSelector(state)
  }));

  const [clientSecret, setClientSecret] = useState<string>('');
  const [membershipPricingPlan, setMembershipPricingPlan] = useState<PricingPlanSchema | null>(null);

  const options: StripeElementsOptions = {
    // clientSecret is fetched from backend
    // paymentIntentClientSecret is taken from the return_url
    clientSecret: clientSecret || paymentIntentClientSecret,
    appearance
  };

  // Redirect to homepage if the user is already a member
  useEffect(() => {
    if (!paymentIntentClientSecret && isMember) {
      router.replace('/');
    }
  }, [isMember, paymentIntentClientSecret]);

  // Redirect to homepage if it is own channel
  useEffect(() => {
    if (profileChannelName === channelName) {
      router.replace(getChannelURL(channelName));
    }
  }, [profileChannelName, channelName]);

  useEffect(() => {
    if (channelName) {
      fetchMembershipPricingPlan();
    }

    // paymentIntentClientSecret is taken from Stripe return_url after payment is confirmed.
    // If there is a paymentIntentClientSecret, it means that user completed the payment and no need to fetch client secret from backend.
    if (!paymentIntentClientSecret) {
      fetchClientSecret();
    }
  }, [channelName, paymentIntentClientSecret]);

  const fetchMembershipPricingPlan = async () => {
    try {
      const response = await api.requests.ltv.getChannelByName(channelName || '');

      setMembershipPricingPlan(response.data?.membershipPricingPlan);
    } catch (error) {
      LoggerService.log('FETCH_MEMBERSHIP_PRICING_PLAN', error);
    }
  };

  const fetchClientSecret = async () => {
    try {
      // get Stripe PaymentIntent client secret from backend
      const response = await api.requests.ltv.prepareMembershipPayment(channelName || '');

      setClientSecret(response.data);
    } catch (error) {
      LoggerService.log('FETCH_CLIENT_SECRET', error);

      if (channelName) {
        router.replace(getChannelURL(channelName));
      }
    }
  };

  const renderContent = () => {
    // when clientSecret and pricingPlan is fetched from backend, payment form is ready to be rendered
    if (clientSecret && membershipPricingPlan) {
      return (
        <Elements options={options} stripe={stripePromise}>
          <div className="mb-24">
            <MembershipPlanCard {...membershipPricingPlan} channelName={channelName || ''} />
          </div>
          <Payment.Form
            paymentType={PaymentType.MEMBER}
            price={membershipPricingPlan?.price * 100}
            pricingPlanId={channelName || ''}
          />
        </Elements>
      );
    }
    // show PaymentComplete component when return_url is passed
    if (paymentIntentClientSecret) {
      return (
        <Elements options={options} stripe={stripePromise}>
          <Payment.Process pricingPlan={membershipPricingPlan} />
        </Elements>
      );
    }

    return <Loader global />;
  };

  return <>{renderContent()}</>;
};

export default MemberPayment;
